import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import SectionHeader from '../ui/SectionHeader';
import { liquiditySources } from '../../constants/content';
import { useSectionView } from '../../hooks/useSectionView';

// Keyed by id rather than display name so the styling survives translation.
// Comet's mark is green on off-white, so it needs a dark tile in both themes.
const logoBackgrounds = {
  stellarClassic: 'bg-white',
  soroswap: 'bg-[#8866DD]',
  comet: 'bg-[#0F1016]'
};

export default function LiquiditySources() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('liquidity_sources', 2);

  return (
    <section
      ref={sectionRef}
      id="liquidity"
      className={`py-24 ${darkMode ? 'bg-main-black' : 'bg-white'}`}
    >
      <div className="mainCont">
        <SectionHeader
          label={t('liquidity.label')}
          title={t('liquidity.headline')}
          titleHighlight={t('liquidity.headlineHighlight')}
          highlightColor="orange"
          subtitle={t('liquidity.subheadline')}
        />

        {/* DEX Grid */}
        {/* Flex with fixed basis so an odd count centers its last row instead of leaving a gap */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-12">
          {liquiditySources.map((source) => (
            <GlassCard
              key={source.id}
              hover
              padding="md"
              className="text-center w-[calc(50%-0.5rem)] md:w-[calc(33.333%-1rem)] lg:w-[calc(20%-1.2rem)]"
            >
              {/* Logo */}
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                logoBackgrounds[source.id] || (darkMode ? 'bg-white/5' : 'bg-gray-100')
              }`}>
                <img
                  src={source.logo}
                  alt={source.name}
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-2xl font-bold text-main">${source.name.charAt(0)}</span>`;
                  }}
                />
              </div>

              {/* Name */}
              <h3 className={`titleFont text-lg font-bold mb-1 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {source.name}
              </h3>

              {/* Description */}
              <p className={`text-sm mb-3 ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t(`liquidity.sources.${source.id}.description`)}
              </p>

              {/* Status */}
              <div className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className={`text-xs font-medium ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`}>
                  {t('liquidity.live')}
                </span>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* Bottom text */}
        <div className="text-center">
          <p className={`text-lg font-medium mb-2 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('liquidity.bottomText')}
          </p>
          <p className={`text-sm italic ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('liquidity.bottomSubtext')}
          </p>
        </div>
      </div>
    </section>
  );
}
